// Tracks translation keys used during SSR/CSR and compares them against loaded keys.
// SSR uses AsyncLocalStorage so concurrent Express requests cannot share tracker state.

interface UsedKeyTracker {
  usedKeys: Set<string>;
  routeTags: string[];
  language: string;
  pageName: string;
}

interface TrackingContext {
  tracker: UsedKeyTracker | null;
  returnedKeys: Record<string, string> | null;
}

type AlsLike<T> = {
  run: <R>(store: T, callback: (...args: any[]) => R, ...args: any[]) => R;
  getStore: () => T | undefined;
  enterWith: (store: T) => void;
};

// AsyncLocalStorage is Node-only — never import it into the client bundle.
let als: AlsLike<TrackingContext> | null = null;
if (typeof __SERVER__ !== "undefined" && __SERVER__) {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
    const { AsyncLocalStorage } = require("async_hooks") as {
      AsyncLocalStorage: new <T>() => AlsLike<T>;
    };
    als = new AsyncLocalStorage<TrackingContext>();
  } catch {
    als = null;
  }
}

// Client SPA (and SSR fallback when ALS is unavailable) uses a process-local context.
const fallbackContext: TrackingContext = {
  tracker: null,
  returnedKeys: null,
};

const getContext = (): TrackingContext | null => {
  if (als) {
    // Outside beginTranslationTracking, ignore tracking to avoid cross-request leaks.
    return als.getStore() ?? null;
  }
  return fallbackContext;
};

/**
 * Start a request-scoped tracking context for SSR.
 * Uses enterWith so we don't have to wrap/re-indent the whole SSR handler.
 * Safe for Express async handlers: each request continues on this async chain.
 */
export const beginTranslationTracking = (): void => {
  const store: TrackingContext = { tracker: null, returnedKeys: null };
  if (als) {
    als.enterWith(store);
    return;
  }
  fallbackContext.tracker = null;
  fallbackContext.returnedKeys = null;
};

export const setReturnedTranslationKeys = (keys: Record<string, string>): void => {
  const ctx = getContext();
  if (ctx) {
    ctx.returnedKeys = keys;
  }
};

// init tracking new ssr/csr request
export const initializeKeyTracking = (
  routeTags: string[],
  language: string,
  pageName: string,
): void => {
  const ctx = getContext();
  if (!ctx) return;

  ctx.tracker = {
    usedKeys: new Set<string>(),
    routeTags,
    language,
    pageName,
  };
};

export const trackKeyUsage = (key: string, isDynamic = false): void => {
  // Skip tracking for dynamic keys - they won't trigger Sentry alerts
  if (isDynamic) return;

  const ctx = getContext();
  if (ctx?.tracker && key && typeof key === "string") {
    ctx.tracker.usedKeys.add(key);
  }
};

// check for dynamic/static - requireDot: true for critical keys only
export const isValidTranslationKey = (key: string, requireDot = false): boolean => {
  // Valid keys----> dot notation, lowercase with dots

  if (!key || key.length < 3) return false;

  if (
    key.includes(" ") ||
    /^[A-Z]/.test(key) ||
    /^[0-9]/.test(key) ||
    key.includes("@") ||
    key.includes("+") ||
    /^https?:\/\//.test(key) ||
    key.length > 100
  ) {
    return false;
  }

  if (requireDot) {
    return key.includes(".");
  }

  if (!key.includes(".")) {
    return /^[a-z]+$/.test(key);
  }

  return true;
};

const isDotFormattedKey = (key: string): boolean => key.includes(".");

type TranslationAlertOptions = {
  level: "warning" | "error";
  tags: Record<string, string>;
  extra: Record<string, unknown>;
  fingerprint?: string[];
};

/** Send translation alerts from SSR (@sentry/node) or CSR (window.Sentry). */
const captureTranslationAlert = (message: string, options: TranslationAlertOptions): void => {
  if (typeof window !== "undefined" && typeof window.Sentry !== "undefined") {
    window.Sentry.captureMessage(message, options);
    return;
  }

  if (typeof global !== "undefined" && __SERVER__) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
      const Sentry = require("@sentry/node");
      Sentry.captureMessage(message, options);
    } catch {
      // Sentry not initialized — server logs remain the fallback
    }
  }
};

export const reportEmptyTranslationLoad = ({
  language,
  routeTags,
  pageName,
  source,
}: {
  language: string;
  routeTags: string[];
  pageName: string;
  source: "ssr" | "csr";
}): void => {
  captureTranslationAlert("Critical: Translation load returned zero keys", {
    level: "error",
    fingerprint: ["empty-translation-load", pageName, source],
    tags: {
      page: pageName,
      language,
      source,
      routeTags: routeTags.join(","),
      severity: "critical",
    },
    extra: {
      type: "EMPTY_TRANSLATION_LOAD",
      language,
      routeTags,
      page: pageName,
      source,
      timestamp: new Date().toISOString(),
    },
  });
};

export const getCurrentTracker = (): UsedKeyTracker | null => getContext()?.tracker ?? null;

export const compareAndAlert = (
  returnedKeys: Record<string, string>,
  isLoadingTranslations = false,
): void => {
  const tracker = getContext()?.tracker;
  if (!tracker) {
    return;
  }

  // Skip Sentry alerts if translations are still loading to avoid false positives
  if (isLoadingTranslations) {
    return;
  }

  const { usedKeys, routeTags, language, pageName } = tracker;
  const returnedKeySet = new Set(Object.keys(returnedKeys));

  // Separate valid translation keys from dynamic/hardcoded strings
  const allUsedKeys = Array.from(usedKeys);
  const validKeys = allUsedKeys.filter((key) => isValidTranslationKey(key));
  // const invalidKeys = allUsedKeys.filter((key) => !isValidTranslationKey(key)); //keeping this for future usage

  // Find missing keys (valid keys that aren't returned by Tolgee)
  const missingKeys = validKeys.filter((key) => !returnedKeySet.has(key));
  const missingDotFormattedKeys = missingKeys.filter(isDotFormattedKey);

  // const foundKeys = validKeys.filter((key) => returnedKeySet.has(key));  // can be used to list all the found keys from tolgee

  if (typeof window !== "undefined" && (window as any).__reduxDispatch) {
    (window as any).__reduxDispatch({
      type: "appData/setMissingTranslationKeys",
      payload: missingKeys,
    });
  }

  if (missingKeys.length > 0) {
    const alertPayload = {
      type: "MISSING_TRANSLATION_KEYS",
      page: pageName,
      language,
      routeTags,
      missingKeys,
      validKeyCount: validKeys.length,
      returnedKeyCount: returnedKeySet.size,
      timestamp: new Date().toISOString(),
    };

    captureTranslationAlert("Missing Translation Keys Detected", {
      level: "warning",
      tags: {
        page: pageName,
        language,
        missingKeyCount: String(missingKeys.length),
      },
      extra: alertPayload,
    });
  }

  // critical alert for missing keys
  if (missingDotFormattedKeys.length > 0) {
    const dotFormattedAlertPayload = {
      type: "MISSING_DOTFORMATTED_TRANSLATION_KEYS",
      page: pageName,
      language,
      routeTags,
      missingDotFormattedKeys,
      missingKeyCount: missingDotFormattedKeys.length,
      timestamp: new Date().toISOString(),
    };

    captureTranslationAlert("Critical: Missing Dot-Formatted Translation Keys", {
      level: "error",
      fingerprint: ["missing-translation-keys", pageName, missingDotFormattedKeys.join(",")],
      tags: {
        page: pageName,
        language,
        missingKeyCount: String(missingDotFormattedKeys.length),
        missingDotFormattedKeys: missingDotFormattedKeys.join(","),
        routeTags: routeTags.join(","),
        severity: "critical",
      },
      extra: dotFormattedAlertPayload,
    });
  }
};

export const performPostRenderComparison = (): void => {
  const ctx = getContext();
  if (!ctx?.returnedKeys) {
    resetTracking();
    return;
  }

  compareAndAlert(ctx.returnedKeys);
  resetTracking();
};

export const resetTracking = (): void => {
  const ctx = getContext();
  if (!ctx) return;
  ctx.tracker = null;
  ctx.returnedKeys = null;
};

//  Client-side tracking for SPA navigation
export const initializeClientSideTracking = (
  routeTags: string[],
  language: string,
  pageName: string,
): void => {
  initializeKeyTracking(routeTags, language, pageName);
};

export const performClientSideComparison = (
  returnedKeys: Record<string, string>,
  getIsLoading?: () => boolean,
): void => {
  setTimeout(() => {
    if (getContext()?.tracker) {
      const isLoading = getIsLoading ? getIsLoading() : false;
      compareAndAlert(returnedKeys, isLoading);
      resetTracking();
    }
  }, 3000);
};
